import { openInMacAppStore } from '../../../../util/mac-app-store';
import { Component } from '@angular/core';
import { STORE_APPS, storeAppListPrice } from 'src/app/data/product/product-lookup.data';
import { URLS } from 'src/app/data/navigation/navigation.data';
import { AnalyticsService } from 'src/app/service/analytics/analytics.service';
import { CartPopupService } from 'src/app/service/cart/cart-popup.service';
import { StoreService } from 'src/app/service/store/store.service';
import { PRODUCT_HEROES } from '../product-hero.data';
import { ProductShots, Shot } from '../product-shots';

// Promo landing page for AI Token Meter for macOS, served at /plugins/macos/ai-token-meter: the
// tokens Claude Code, Codex and Antigravity CLI use and what they would cost at API prices, in the
// menu bar. The Mac app the AI Cost Calculator points at. Sold on the site (the cart) and, later, on
// the Mac App Store. SEO is applied by the NavigationInterceptor from navigation.data.ts (client)
// and seo-route-meta.js (server); the FAQ below is word for word AI_TOKEN_METER_FAQ there. Every
// picture is the app in its demo mode (made-up projects), never anyone's real use.
@Component( {
  selector: 'app-macos-ai-token-meter-page',
  templateUrl: './macos-ai-token-meter-page.component.html',
  styleUrls: [ '../chrome-color-picker-page/chrome-color-picker-page.component.scss', '../macos-tidy-desktop-page/macos-tidy-desktop-page.component.scss', '../macos-color-picker-page/macos-color-picker-page.component.scss', '../product-page.scss' ],
} )
export class MacosAiTokenMeterPageComponent extends ProductShots {

  readonly URLS = URLS;
  // Sold on codigrate.com only: the Mac App Store refused it (4.3, 2026-10-28), so no App Store button and
  // no Coming Soon (owner). released stays false; the App Store markup is kept for a later listing.
  readonly released = false;
  readonly appStoreUrl = '';
  readonly storeSku = 'ai-token-meter/macos';
  readonly hero = PRODUCT_HEROES[ 'macos-ai-token-meter' ];
  readonly facts = [ 'Native Menu Bar App', 'macOS 14 Or Later', 'Apple Silicon' ];

  readonly features = [
    {
      title: 'Today in Your Menu Bar',
      description: 'Today’s tokens and what they would cost at API prices, live in the menu bar, from Claude Code, Codex and Antigravity CLI.',
    },
    {
      title: 'Every Model Priced',
      description: 'Each request priced at its maker’s own API rates, cache reads and writes included. The price list updates once a day, by itself.',
    },
    {
      title: 'Limits at a Glance',
      description: 'Claude’s 5-hour window with the time left in it, and Codex’s own limits as Codex reports them.',
    },
    {
      title: 'Working Now',
      description: 'The sessions at work right now, each with its project, its model and what it has spent today.',
    },
    {
      title: 'Your History',
      description: 'Yesterday, this week, this month or all time, by session, by model or by project.',
    },
    {
      title: 'Share Your Month',
      description: 'One click makes a picture of the work your agents did, with what it would have cost at API prices.',
    },
    {
      title: 'Private by Design',
      description: 'It reads only the token counts your agents write in their own logs, in folders you choose. Never your prompts or the answers, no account, and nothing leaves your Mac.',
    },
  ];

  readonly highlights = this.features.slice( 0, 3 );
  readonly more = this.features.slice( 3 );

  readonly shots: Shot[] = [
    {
      name: 'Every Token in Your Menu Bar',
      src: './assets/product-lines/tools/ai-token-meter/screenshot-menu-bar.webp',
      large: './assets/product-lines/tools/ai-token-meter/screenshot-menu-bar-large.webp',
      alt: 'The AI Token Meter panel: today’s tokens and their cost at API prices, the limits and the sessions working now',
    },
    {
      name: 'See What Each Model Costs',
      src: './assets/product-lines/tools/ai-token-meter/screenshot-models.webp',
      large: './assets/product-lines/tools/ai-token-meter/screenshot-models-large.webp',
      alt: 'This month in AI Token Meter by model: Claude, GPT and Gemini models with their tokens and cost',
    },
    {
      name: 'Know When Limits Reset',
      src: './assets/product-lines/tools/ai-token-meter/screenshot-limits.webp',
      large: './assets/product-lines/tools/ai-token-meter/screenshot-limits-large.webp',
      alt: 'Claude’s 5-hour window and the time left in it, in AI Token Meter',
    },
    {
      name: 'Connect Your AI Agents',
      src: './assets/product-lines/tools/ai-token-meter/screenshot-connect.webp',
      large: './assets/product-lines/tools/ai-token-meter/screenshot-connect-large.webp',
      alt: 'AI Token Meter’s Agents settings with Claude Code, Codex and Antigravity CLI connected',
    },
  ];

  // Word for word AI_TOKEN_METER_FAQ in seo-route-meta.js (the crawlable copy and its FAQPage).
  readonly faq = [
    {
      q: 'Which coding agents does it read?',
      a: 'Claude Code, Codex and Antigravity CLI. You connect each in Settings by choosing its folder in the macOS open panel, and the meter reads the logs that agent already writes.',
    },
    {
      q: 'Is the cost what I pay?',
      a: 'It is what the tokens would cost on the pay-as-you-go API of each model’s maker. Claude Pro and Max and ChatGPT plans don’t charge per token, so the number is the value of the work done, not your bill.',
    },
    {
      q: 'Does it read my prompts or code?',
      a: 'No. It takes only the token counts, the model, the time and the project folder of each request. It never reads prompts, answers or tool calls, writes nothing into the agents’ folders, and sends nothing anywhere.',
    },
    {
      q: 'Where do the prices come from?',
      a: 'From LiteLLM’s public model price list, the one codigrate.com’s AI Cost Calculator shows. The app downloads it once a day, and you can turn that off.',
    },
  ];

  constructor(
    private analyticsService: AnalyticsService,
    private cartPopupService: CartPopupService,
    private storeService: StoreService,
  ) {
    super();
  }

  /** the store sells the app on the site: the cart is the first button */
  get sellsHere(): boolean {
    return !!STORE_APPS[ this.storeSku ] && this.storeService.availability( this.storeSku ) === 'buy';
  }

  /** "$3.99": the live Stripe price, else the list price */
  get priceLabel(): string | null {
    return this.storeService.priceLabel( this.storeSku, storeAppListPrice( this.storeSku ) );
  }

  addToCart(): void {
    this.cartPopupService.add( this.storeSku, 'macos-ai-token-meter-page' );
  }

  onInstall( event?: Event ): void {
    this.analyticsService.logEvent( 'App Install Clicked', { app: 'macos-ai-token-meter' } );
    openInMacAppStore( event, this.appStoreUrl );
  }
}
